<template>
    <div class="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
            <img class="mx-auto h-16 w-auto" :src="require('@/assets/images/company_logo.png')" alt="회사 로고" />
            <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">
                비밀번호 찾기
            </h2>
            <p class="mt-2 text-center text-sm text-gray-600">
                매장코드와 등록된 휴대폰 번호로 본인확인을 진행합니다
            </p>
        </div>

        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-200">
                <form class="space-y-6" @submit.prevent="handleSubmit">
                    <!-- 매장코드 입력 -->
                    <div>
                        <label for="branchCode" class="block text-sm font-medium text-gray-700">
                            매장코드
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <UserIcon class="h-5 w-5 text-gray-400" />
                            </div>
                            <input id="branchCode" name="branchCode" type="text" pattern="[0-9]{6}" maxlength="6"
                                required v-model="branchCode" @input="onBranchCodeInput"
                                :class="{ 'border-red-300': errors.branchCode }"
                                class="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="123456" />
                        </div>
                        <p v-if="errors.branchCode" class="mt-2 text-sm text-red-600">{{ errors.branchCode }}</p>
                    </div>

                    <!-- 휴대폰 번호 입력 -->
                    <div>
                        <label for="phoneNumber" class="block text-sm font-medium text-gray-700">
                            휴대폰 번호
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <PhoneIcon class="h-5 w-5 text-gray-400" />
                            </div>
                            <input id="phoneNumber" name="phoneNumber" type="tel" required v-model="phoneNumber"
                                @input="formatPhoneNumber" :class="{ 'border-red-300': errors.phoneNumber }"
                                class="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="010-0000-0000" />
                        </div>
                        <p v-if="errors.phoneNumber" class="mt-2 text-sm text-red-600">{{ errors.phoneNumber }}</p>
                    </div>

                    <div>
                        <button type="submit" :disabled="isLoading"
                            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <LoaderIcon v-if="isLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                            <span v-if="isLoading">확인 중...</span>
                            <span v-else>본인확인</span>
                        </button>
                    </div>
                </form>

                <div class="mt-6">
                    <div class="relative">
                        <div class="absolute inset-0 flex items-center">
                            <div class="w-full border-t border-gray-300"></div>
                        </div>
                        <div class="relative flex justify-center text-sm">
                            <span class="px-2 bg-white text-gray-500">
                                또는
                            </span>
                        </div>
                    </div>

                    <div class="mt-6">
                        <router-link to="/login"
                            class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            로그인으로 돌아가기
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { UserIcon, PhoneIcon, LoaderIcon } from 'lucide-vue-next'
import { authAPI } from '@/api/apiClient'

const router = useRouter()
const branchCode = ref('')
const phoneNumber = ref('')
const isLoading = ref(false)
const errors = reactive({
  branchCode: '',
  phoneNumber: ''
})

const onBranchCodeInput = (event) => {
  // 숫자만 입력 가능
  event.target.value = event.target.value.replace(/[^0-9]/g, '')
  branchCode.value = event.target.value
}

const formatPhoneNumber = (event) => {
  // 숫자만 추출
  let number = event.target.value.replace(/[^0-9]/g, '')
  
  // 길이에 따라 포맷팅
  if (number.length <= 3) {
    phoneNumber.value = number
  } else if (number.length <= 7) {
    phoneNumber.value = number.slice(0, 3) + '-' + number.slice(3)
  } else {
    phoneNumber.value = number.slice(0, 3) + '-' + number.slice(3, 7) + '-' + number.slice(7, 11)
  }
}

const validateForm = () => {
  let isValid = true
  errors.branchCode = ''
  errors.phoneNumber = ''

  if (!branchCode.value) {
    errors.branchCode = '매장코드를 입력해주세요.'
    isValid = false
  } else if (!/^\d{6}$/.test(branchCode.value)) {
    errors.branchCode = '매장코드는 6자리 숫자여야 합니다.'
    isValid = false
  }

  if (!phoneNumber.value) {
    errors.phoneNumber = '휴대폰 번호를 입력해주세요.'
    isValid = false
  } else if (!/^010-\d{4}-\d{4}$/.test(phoneNumber.value)) {
    errors.phoneNumber = '올바른 휴대폰 번호 형식이 아닙니다.'
    isValid = false
  }

  return isValid
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isLoading.value = true
  try {
    const cleanPhoneNumber = phoneNumber.value.replace(/-/g, '')
    console.log('Sending:', { branchCode: branchCode.value, phone: cleanPhoneNumber })
    
    const response = await authAPI.verifyBranchAndPhone(branchCode.value, cleanPhoneNumber)
    console.log('Verification response:', response.data) // 토큰 확인

    router.push({
      name: 'ResetPassword',
      query: { 
        branchCode: branchCode.value,
        verified: 'true',
        token: response.data.data.verification_token
      }
    })
  } catch (error) {
    console.error('Error details:', error.response?.data || error)
    if (error.response?.data) {
      const { message } = error.response.data
      errors.branchCode = message
    } else {
      errors.branchCode = '서버와의 통신 중 오류가 발생했습니다.'
    }
  } finally {
    isLoading.value = false
  }
}
</script>